export type BrandKey = 'yansar' | 'taxsaints' | 'fiscalsaints' | 'monument'

export interface BrandTheme {
  key: BrandKey
  name: string
  primaryColor: string
  accentColor: string
  bgColor: string
  textColor: string
  fontFamily: string
}

// Fallback defaults — overridden by GET /api/brand/{brand_key} at runtime
export const BRAND_DEFAULTS: Record<BrandKey, BrandTheme> = {
  yansar: {
    key: 'yansar',
    name: 'Yansar Consulting',
    primaryColor: '#1e3a5f',
    accentColor: '#c9a84c',
    bgColor: '#ffffff',
    textColor: '#1a1a1a',
    fontFamily: 'Inter, sans-serif',
  },
  taxsaints: {
    key: 'taxsaints',
    name: 'Tax Saint Services',
    primaryColor: '#2d6a4f',
    accentColor: '#74c69d',
    bgColor: '#ffffff',
    textColor: '#1a1a1a',
    fontFamily: 'Inter, sans-serif',
  },
  fiscalsaints: {
    key: 'fiscalsaints',
    name: 'Fiscal Saints',
    primaryColor: '#023e8a',
    accentColor: '#90e0ef',
    bgColor: '#ffffff',
    textColor: '#1a1a1a',
    fontFamily: 'Inter, sans-serif',
  },
  monument: {
    key: 'monument',
    name: 'Monument Financial Services',
    primaryColor: '#4a1942',
    accentColor: '#c77dff',
    bgColor: '#ffffff',
    textColor: '#1a1a1a',
    fontFamily: 'Inter, sans-serif',
  },
}

/**
 * Resolve brand key from: (1) ?brand= query param, (2) subdomain, (3) default 'yansar'
 */
export function resolveBrandKey(): BrandKey {
  // 1. Query param
  const params = new URLSearchParams(window.location.search)
  const paramBrand = params.get('brand') as BrandKey | null
  if (paramBrand && paramBrand in BRAND_DEFAULTS) return paramBrand

  // 2. Subdomain
  const host = window.location.hostname
  if (host.includes('taxsaint')) return 'taxsaints'
  if (host.includes('fiscalsaint')) return 'fiscalsaints'
  if (host.includes('monument')) return 'monument'

  // 3. Default
  return 'yansar'
}

export function applyBrandCssVars(theme: Partial<BrandTheme>): void {
  const root = document.documentElement
  // Only write values that are actually usable. `setProperty(name, undefined)`
  // stores the literal string "undefined", which makes `var(--color-primary)`
  // an invalid colour — `bg-primary` then resolves to transparent and every
  // default Button becomes white-on-white. A brand row with a null colour, or
  // a 200 with a partial body, must fall back to the defaults already applied,
  // never blank the theme.
  const vars: Array<[string, unknown]> = [
    ['--color-primary', theme.primaryColor],
    ['--color-accent', theme.accentColor],
    ['--color-bg', theme.bgColor],
    ['--color-text', theme.textColor],
    ['--font-family', theme.fontFamily],
  ]
  for (const [name, value] of vars) {
    if (typeof value === 'string' && value.trim() !== '') {
      root.style.setProperty(name, value)
    }
  }
}
